import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import type { Organization, OrganizationBranding } from '../models/Organization';

interface UseCurrentOrganizationResult {
  organization: Organization | null;
  loading: boolean;
  error: Error | null;
}

export function useCurrentOrganization(organizationId: string | null): UseCurrentOrganizationResult {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState<boolean>(!!organizationId);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!organizationId) {
      setOrganization(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    let unsubscribe: (() => void) | null = null;

    const orgRef = doc(db, 'clients', organizationId);
    unsubscribe = onSnapshot(
      orgRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setOrganization(null);
          setError(new Error('Client not found'));
          setLoading(false);
          return;
        }

        const data = snapshot.data() as any;

        const branding: OrganizationBranding | undefined = data.branding
          ? {
              ...data.branding,
              logoUpdatedAt: data.branding.logoUpdatedAt?.toDate
                ? data.branding.logoUpdatedAt.toDate()
                : data.branding.logoUpdatedAt,
            }
          : undefined;

        const org: Organization = {
          id: snapshot.id,
          entityType: data.entityType || 'client',
          name: data.name || '',
          address: data.address,
          status: data.status,
          clientIds: data.clientIds || data.projectIds || [],
          projectIds: data.projectIds || data.clientIds || [],
          email: data.email,
          phone: data.phone,
          settings: data.settings,
          branding,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        };

        setOrganization(org);
        setError(null);
        setLoading(false);
      },
      (err) => {
        console.error('Error subscribing to client:', err);
        setError(err as Error);
        setOrganization(null);
        setLoading(false);
      }
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [organizationId]);

  return { organization, loading, error };
}
